import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';

interface PaymentsByClientRow {
  type: 'client' | 'cashReceipt' | 'detail';
  cells: string[];
  depth: number;
}

@Component({
  selector: 'app-custom-table-report-payments-by-client',
  templateUrl: './custom-table-report-payments-by-client.component.html',
  styleUrls: ['./custom-table-report-payments-by-client.component.scss'],
  standalone: true,
  imports: [CommonModule, ButtonModule, TableModule, DropdownModule, FormsModule],
  providers: [ConfirmationService]
})
export class CustomTableReportPaymentsByClientComponent implements AfterViewInit, OnDestroy, OnChanges {

  @Input() dataReport: CustomTableReport;
  @Input() filtersHeight: number = 0;
  @ViewChild('headerContainer') headerContainer!: ElementRef;

  reportRows: PaymentsByClientRow[] = [];
  resizeObserver?: ResizeObserver;
  scrollHeight: string = '0px';

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataReport'] && this.dataReport?.data) {
      this.buildRowsFromDataReport();
    }
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateScrollHeight();
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  private initResizeObserver() {
    if (this.headerContainer?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollHeight();
      });
      this.resizeObserver.observe(this.headerContainer.nativeElement);
    }
  }

  private updateScrollHeight() {
    const headerHeight = this.headerContainer?.nativeElement?.offsetHeight || 0;
    this.scrollHeight = `${window.innerHeight - headerHeight - this.filtersHeight - 65}px`;
    this.cdr.detectChanges();
  }

  getBackgroundColor(depth: number): string {
    const startColor = [200, 200, 200];
    const endColor = [244, 244, 244];

    const r = Math.round(startColor[0] + (endColor[0] - startColor[0]) * (depth / 3));
    const g = Math.round(startColor[1] + (endColor[1] - startColor[1]) * (depth / 3));
    const b = Math.round(startColor[2] + (endColor[2] - startColor[2]) * (depth / 3));

    return `rgb(${r}, ${g}, ${b})`;
  }

  isDecimal(value: any): boolean {
    const includesDot = value?.toString()?.includes('.');

    if (!includesDot) return false;
    if (isNaN(Number(value))) return false;

    return true;
  }

  alignColumnText(value: any): string {
    if (this.isDecimal(value)) return 'right';
    if (isNaN(Number(value))) return 'center';

    return 'center'
  }

  private buildRowsFromDataReport(): void {
    if (!this.dataReport?.data || !Array.isArray(this.dataReport.data)) {
      this.reportRows = [];
      return;
    }

    this.reportRows = this.buildReportRows(this.dataReport.data, 1);
  }

  private buildReportRows(nodes: any[], depth: number): PaymentsByClientRow[] {
    const result: PaymentsByClientRow[] = [];

    for (const node of nodes) {
      const mapped = this.mapNodeToRow(node, depth);
      result.push(mapped);

      if (node.children && Array.isArray(node.children) && node.children.length) {
        const childRows = this.buildReportRows(node.children, depth + 1);
        result.push(...childRows);
      }
    }
    return result;
  }

  private mapNodeToRow(node: any, depth: number): PaymentsByClientRow {
    switch (node.type) {
      case 'client':
        return {
          type: 'client',
          depth,
          cells: [
            node.name ?? '',
            `Anticipos: ${node.advancesValue ?? '0.00'}`,
            `Aplicado: ${node.appliedValue ?? '0.00'}`,
            `Saldo: ${node.residueValue ?? '0.00'}`
          ]
        };
      case 'cashReceipt':
        return {
          type: 'cashReceipt',
          depth,
          cells: [
            node.consecutive ?? '',
            node.date ?? '',
            node.concept ?? '',
            node.paidValue ?? '',
            node.advancesValue ?? '',
            node.refundValue ?? '',
            node.utilizationValue ?? '',
            node.commissionValue ?? '',
            node.totalValue ?? '',
            node.appliedValue ?? '',
            node.residueValue ?? ''
          ]
        };
      case 'detail':
      default:
        return {
          type: 'detail',
          depth,
          cells: [
            node.document ?? '',
            node.taxValue ?? '',
            node.discountValue ?? '',
            node.paidValue ?? ''
          ]
        };
    }
  }

}
